import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { clearCurrentPermitData } from 'src/redux-store/slices/currentPermitSlice';
import { clearNavigationDetails } from 'src/redux-store/slices/navigationSlice';
import { clearTownshipData } from 'src/redux-store/slices/townshipSlice';
import { clearUserDetails } from 'src/redux-store/slices/userSlice';
import { persistor } from '../redux-store/appStore';
import { clearCart } from 'src/redux-store/slices/cartSlice';
import getEnvVars from 'src/config/env';
import { httpRequest } from 'src/services/httpRequest';

export const REMEMBER_ME_COOKIE = 'rememberMe';
export const REMEMBER_EMAIL_COOKIE = 'email';
const LEGACY_PASSWORD_COOKIE = 'password';

/** Removes legacy password cookie only; passwords must never be stored client-side. */
export const clearLegacyPasswordCookie = () => {
  Cookies.remove(LEGACY_PASSWORD_COOKIE);
};

/** Clears Remember Me when the user opts out on login. */
export const clearRememberMeCookies = () => {
  Cookies.remove(REMEMBER_ME_COOKIE);
  Cookies.remove(REMEMBER_EMAIL_COOKIE);
  clearLegacyPasswordCookie();
};

/** Remember Me: email/username only (30 days). */
export const persistRememberMeEmail = (email: string) => {
  Cookies.set(REMEMBER_ME_COOKIE, 'true', { expires: 30 });
  Cookies.set(REMEMBER_EMAIL_COOKIE, encodeURIComponent(email), { expires: 30 });
  clearLegacyPasswordCookie();
};

export const loadRememberMeEmail = (): string | null => {
  if (Cookies.get(REMEMBER_ME_COOKIE) !== 'true') {
    return null;
  }
  const stored = Cookies.get(REMEMBER_EMAIL_COOKIE);
  return stored ? decodeURIComponent(stored) : null;
};

const { apiUrl } = getEnvVars()

export const getAccessToken = () => localStorage.getItem('accessToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

export const setAccessToken = (token: string) =>
  localStorage.setItem('accessToken', token);
export const setRefreshToken = (token: string) =>
  localStorage.setItem('refreshToken', token);

// Utility to clear auth tokens and session data (Remember Me email may remain).
const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.clear();
  sessionStorage.clear();
  clearLegacyPasswordCookie();
  document.cookie =
    'Login_Success=false; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  persistor.purge();
};

// Redux state clearing function
const clearReduxState = (dispatch: any) => {
  dispatch(clearUserDetails());
  dispatch(clearTownshipData());
  dispatch(clearCurrentPermitData());
  dispatch(clearNavigationDetails());
  dispatch(clearCart());
};

// Function to logout the user (no hooks)
export const logoutUser = async (dispatch: any, navigate: any) => {
	try {
		const response = await httpRequest(
			'POST',
			'/auth/logout',
		);

		// Check API success
		if (response?.status === 'success') {
			// Reset Redux state
			clearReduxState(dispatch);

			// Clear non-httpOnly storage (local/session)
			clearTokens();

			// Redirect to login
			navigate('/');
		} else {
			console.warn('Logout failed:', response?.message);

			// Still force client-side cleanup (safe fallback)
			clearReduxState(dispatch);
			clearTokens();
			navigate('/');
		}
	} catch (error) {
		console.error('Logout API error:', error);

		// Fallback cleanup (important for expired sessions)
		clearReduxState(dispatch);
		clearTokens();
		navigate('/');
	}
};

// Function to handle token refresh
export const handleTokenRefresh = async (): Promise<string> => {
  try {
    const response = await axios.get(`${apiUrl}/auth/refresh-token`, {
      withCredentials: true // Ensure cookies are sent with the request
    });
    const { accessToken } = response.data;
    setAccessToken(accessToken);
    return accessToken;
  } catch (error) {
    throw new Error('Unable to refresh token. Please log in again.');
  }
};

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

/** Clears session and sends the user to the login page (used by axios and fetch callers). */
export const redirectToLoginOnSessionExpired = () => {
  toast.error('Session expired. Please log in again.');
  const existingPath = window.location.pathname;
  localStorage.clear();
  localStorage.setItem('existingLocation', existingPath);
  window.location.href = '/';
};
